import React, { useState } from 'react';
import { BarChart2, Plus, Lock, ArrowRight, Crown, TrendingUp, LineChart, Brain } from 'lucide-react';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { useUserStore } from '../../stores/userStore';
import { useNavigate } from 'react-router-dom';
import type { AnalyticsEntry } from '../../types/analytics';
import AnalyticsForm from './analytics/AnalyticsForm';
import AnalyticsTable from './analytics/AnalyticsTable';
import AnalyticsChart from './analytics/AnalyticsChart';
import AnalyticsInsights from './analytics/AnalyticsInsights';
import CompetitorAnalytics from './analytics/CompetitorAnalytics';
import AdvancedMetrics from './analytics/AdvancedMetrics';

export default function Analytics() {
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AnalyticsEntry | null>(null);
  const { entries, addEntry, updateEntry, deleteEntry, generateInsights } = useAnalyticsStore();
  const { isPremium, isPro } = useUserStore();
  const navigate = useNavigate();
  
  // If user doesn't have premium or pro, show upgrade prompt
  if (!isPremium() && !isPro()) {
    return (
      <div className="space-y-8">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Lock className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Análises Avançadas</h1>
              <p className="text-gray-600">Atualize seu plano para acessar análises detalhadas</p>
            </div>
          </div>

          <div className="text-center py-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-indigo-100 rounded-full opacity-50"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-100 rounded-full opacity-50"></div>
            
            <div className="relative max-w-md mx-auto">
              <BarChart2 className="h-16 w-16 text-indigo-400 mx-auto mb-6 animate-float" />
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Desbloqueie Análises Poderosas
              </h2>
              <p className="text-gray-600 mb-8">
                Acompanhe o desempenho do seu conteúdo, obtenha insights valiosos e tome decisões baseadas em dados.
              </p>
              <button
                onClick={() => navigate('/dashboard/precos')}
                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors transform hover:scale-105"
              >
                Ver Planos Premium
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <div className="grid grid-cols-3 gap-4 mt-10">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">Análise de Tendências</p>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                    <LineChart className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">Gráficos Detalhados</p>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                    <Brain className="h-5 w-5 text-purple-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">Insights com IA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (data: Omit<AnalyticsEntry, 'id' | 'date'>) => {
    if (editingEntry) {
      updateEntry(editingEntry.id, data);
    } else {
      addEntry(data);
    }
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleEdit = (entry: AnalyticsEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta entrada?')) {
      deleteEntry(id);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <BarChart2 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Análises</h1>
              <p className="text-gray-600">
                {isPro() 
                  ? 'Análises avançadas com tracking de competidores'
                  : 'Acompanhe o desempenho do seu conteúdo'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors transform hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Dados
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-100">
            <BarChart2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum dado analítico ainda
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Adicione dados de desempenho do seu conteúdo para visualizar métricas e obter insights valiosos.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Primeiros Dados
            </button>
          </div>
        ) : (
          <>
            {/* Basic Analytics (Premium & Pro) */}
            <div className="space-y-8">
              <AnalyticsChart entries={entries} />
              <AnalyticsInsights insights={generateInsights()} />
              <AnalyticsTable 
                entries={entries}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>

            {/* Advanced Analytics (Pro Only) */}
            {isPro() && (
              <>
                <div className="border-t border-gray-200 mt-12 pt-12">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Crown className="h-5 w-5 text-amber-500 mr-2" />
                    Análises Avançadas
                    <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full">PRO</span>
                  </h2>
                  <div className="space-y-8">
                    <CompetitorAnalytics />
                    <AdvancedMetrics entries={entries} />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {showForm && (
          <AnalyticsForm
            entry={editingEntry}
            onSubmit={handleSubmit}
            onClose={() => {
              setShowForm(false);
              setEditingEntry(null);
            }}
          />
        )}
      </div>
    </div>
  );
}